import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Skeleton,
  Paper,
  useTheme,
  Container,
  Stack,
} from '@mui/material';

/**
 * TransactionHistoryGridSkeleton - Skeleton para el grid de historial de transacciones
 * Colores del design system: #e9edf2 (light) / #2f363e (dark)
 */

interface TableRowSkeletonProps {
  index: number;
}

const TableRowSkeleton: React.FC<TableRowSkeletonProps> = ({ index }) => {
  const theme = useTheme();
  
  const dateWidths = ['65%', '75%', '70%', '80%', '72%'];
  const descWidths = ['70%', '80%', '75%', '85%', '78%'];
  
  const dateWidth = dateWidths[index % dateWidths.length];
  const descWidth = descWidths[index % descWidths.length];

  const skeletonBg = theme.palette.mode === 'light' 
    ? '#e9edf2' 
    : '#2f363e';
  
  return (
    <TableRow>
      {/* Fecha */}
      <TableCell>
        <Skeleton 
          variant="text" 
          width={dateWidth}
          height={24}
          animation="wave"
          sx={{ backgroundColor: skeletonBg }}
        />
      </TableCell>
      
      {/* Descripción */}
      <TableCell>
        <Skeleton 
          variant="text" 
          width={descWidth}
          height={24}
          animation="wave"
          sx={{ backgroundColor: skeletonBg }}
        />
      </TableCell>
      
      {/* Monto */}
      <TableCell align="right">
        <Skeleton 
          variant="text" 
          width="80%"
          height={24}
          animation="wave"
          sx={{ ml: 'auto', backgroundColor: skeletonBg }}
        />
      </TableCell>
      
      {/* Acciones */}
      <TableCell align="center">
        <Skeleton 
          variant="circular" 
          width={24}
          height={24}
          animation="wave"
          sx={{ mx: 'auto', backgroundColor: skeletonBg }}
        />
      </TableCell>
    </TableRow>
  );
};

const TransactionHistoryGridSkeleton: React.FC = () => {
  const theme = useTheme();
  
  // Usar gris del design system unificado
  const skeletonBg = theme.palette.mode === 'light' 
    ? '#e9edf2' 
    : '#2f363e';

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: theme.palette.mode === 'light' ? '#f6f8fb' : '#10141C',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {/* Header skeleton - simula PageHeader con botón de cerrar */}
      <Paper 
        elevation={0} 
        sx={{ backgroundColor: theme.palette.background.paper }}
      >
        <Container maxWidth="lg">
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ py: 2 }}
          >
            {/* Logo skeleton */}
            <Skeleton 
              variant="rectangular" 
              width={120}
              height={40}
              animation="wave"
              sx={{ 
                backgroundColor: skeletonBg,
                borderRadius: '4px'
              }}
            />
            
            {/* Close button skeleton */}
            <Skeleton 
              variant="circular" 
              width={36}
              height={36}
              animation="wave"
              sx={{ backgroundColor: skeletonBg }}
            />
          </Stack>
        </Container>
      </Paper>

      {/* Content */}
      <Box sx={{ flex: 1, py: 4, px: 3 }}>
        {/* Title skeleton */}
        <Box sx={{ maxWidth: 'lg', mx: 'auto', mb: 3 }}>
          <Skeleton 
            variant="text" 
            width={280}
            height={40}
            animation="wave"
            sx={{ 
              backgroundColor: skeletonBg,
              borderRadius: '8px'
            }}
          />
        </Box>

        <Box sx={{ maxWidth: 'lg', mx: 'auto' }}>
        <TableContainer 
          component={Paper} 
          sx={{ 
            borderRadius: 2,
            boxShadow: 'none',
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: skeletonBg }}>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Fecha
                </TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Descripción
                </TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: 'text.secondary' }}>
                  Monto
                </TableCell>
                <TableCell align="center" sx={{ width: 60 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {[0, 1, 2, 3, 4].map((index) => (
                <TableRowSkeleton key={index} index={index} />
              ))}
            </TableBody>
          </Table>
          </TableContainer>
        </Box>
      </Box>
    </Box>
  );
};

export default TransactionHistoryGridSkeleton;
